
import React, { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { GoogleTagManager } from "@/components/analytics/GoogleTagManager";
import { ScrollToTop } from "@/components/analytics/ScrollToTop";
import { RouteTracker } from "@/components/analytics/RouteTracker";
import { CookieConsent } from "@/components/privacy/CookieConsent";
import { Redirect } from "@/components/Redirect";
import { PreloadLinks } from "@/components/PreloadLinks";
import { routes } from "./routes";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppRoute, ParentRoute, SimpleRoute } from "@/types/routes";

// Google Tag Manager ID
const googleTagId = "GTM-XXXXXXX";

// Add global error handler for module loading errors
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    if (event.message && (
      event.message.includes('Failed to load module script') || 
      event.message.includes('Cannot access') || 
      event.message.includes('Could not establish connection') ||
      event.message.includes('Failed to fetch dynamically imported module') ||
      event.message.includes('Unexpected token')
    )) {
      console.warn('Non-critical module loading error:', event.message);
      // Prevent the error from bubbling up and breaking the app
      event.stopPropagation();
      event.preventDefault();
      return true;
    }
    return false;
  }, true);
  
  // Also handle unhandled promise rejections related to module loading
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && typeof event.reason.message === 'string' && (
      event.reason.message.includes('Failed to load') ||
      event.reason.message.includes('Network error') ||
      event.reason.message.includes('Unexpected token') ||
      event.reason.message.includes('module') ||
      event.reason.message.includes('chunk')
    )) {
      console.warn('Non-critical promise rejection:', event.reason.message);
      // Prevent the error from breaking the app
      event.preventDefault();
      return true;
    }
    return false;
  });
}

const App = () => {
  // Effect for performance monitoring
  useEffect(() => {
    // Measure and report Time to Interactive
    if (typeof window !== 'undefined') {
      try {
        const tti = performance.now();
        console.info(`Time to interactive: ${Math.round(tti)}ms`);
        
        // Report all console errors to help with debugging
        const originalConsoleError = console.error;
        console.error = function(...args) {
          // Still show the original error
          originalConsoleError.apply(console, args);
          
          // You could send these errors to an error tracking service
          // if one is set up in the future
        };
      } catch (err) {
        // Silent fail for performance measurement
      }
    }
  }, []);

  // Helper function to render routes recursively
  const renderRoutes = (routes: AppRoute[]) => {
    return routes.map((route, index) => {
      // Handle parent routes with children
      if ('children' in route) {
        const parentRoute = route as ParentRoute;
        
        // Route has path and children (nested routes)
        if ('path' in parentRoute) {
          return (
            <Route key={index} path={parentRoute.path} element={parentRoute.element}>
              {parentRoute.children.map((childRoute, childIndex) => (
                <Route
                  key={childIndex}
                  path={childRoute.path}
                  element={childRoute.element}
                />
              ))}
            </Route>
          );
        }
        
        // Route has only element and children (layout wrapper)
        return (
          <Route key={index} element={parentRoute.element}>
            {parentRoute.children.map((childRoute, childIndex) => (
              <Route
                key={childIndex}
                path={childRoute.path}
                element={childRoute.element}
              />
            ))}
          </Route>
        );
      }
      
      // Handle simple routes without children
      const simpleRoute = route as SimpleRoute;
      return (
        <Route
          key={index}
          path={simpleRoute.path}
          element={simpleRoute.element}
        />
      );
    });
  };

  return (
    <ErrorBoundary>
      <div className="App">
        {typeof window !== 'undefined' && <GoogleTagManager gtmId={googleTagId} />}
        
        <ScrollToTop />
        <RouteTracker />
        <PreloadLinks />
        
        {/* Redirect component runs first to handle any necessary redirects */}
        <Redirect />
        
        <Routes>
          {renderRoutes(routes)}
        </Routes>
        
        <CookieConsent />
        <Toaster position="top-right" closeButton richColors />
      </div>
    </ErrorBoundary>
  );
};

export default App;
